import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useParams, useRevalidator } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { isAxiosError } from '@components/forms/CreditCardForm/utils';
import { EMAIL_ROUTE_PATHNAME } from '@router/routes/routePathnames';
import { Provider } from '@types';
import getTimeZone from '@utils/datetime/getTimeZone';
import { AllAppointmentsLink } from '../AllAppointmentsLink/AllAppointmentsLink';
import { useAppointmentTimePreference } from '../context/useAppointmentTimePreference';
import { useParseTimeBlockSearchParam, TimeBlock } from '../context/utils';
import { ProviderAvailabilityCard } from '../ProviderAvailabilityCard/ProviderAvailabilityCard';
import { getVisitBlocks } from './utils';
import { handleSelectVisit } from '../../utils';

export function AvailabilitySelector() {
  const { visitTypeId } = useParams();
  const revalidator = useRevalidator();
  const navigate = useNavigate();
  const { groupedAvailabilitiesByTimeBlock } = useAppointmentTimePreference();
  const timeBlock = useParseTimeBlockSearchParam();

  const availabilities = useMemo(() => {
    if (!timeBlock) {
      return;
    }

    const {
      now: immediateAvailabilities,
      today: todaysAvailabilities,
      tomorrow: tomorrowsAvailabilities,
    } = groupedAvailabilitiesByTimeBlock;

    const allTodaysAvailabilities = [
      ...immediateAvailabilities,
      ...todaysAvailabilities,
    ];

    if (timeBlock === 'now') {
      return getVisitBlocks({
        availabilities: immediateAvailabilities,
      });
    }

    if (timeBlock === 'today') {
      return getVisitBlocks({
        availabilities: allTodaysAvailabilities,
        timeBlock,
      });
    }

    if (timeBlock === 'tomorrow') {
      return getVisitBlocks({
        availabilities: tomorrowsAvailabilities,
        timeBlock,
      });
    }

    return null;
  }, [groupedAvailabilitiesByTimeBlock, timeBlock]);

  if (!availabilities) {
    return null;
  }

  async function handleClickAvailability(provider: Provider, time: string) {
    if (!timeBlock || !visitTypeId) {
      return;
    }

    const date = getDateFromTimeBlock(timeBlock);
    const timeZone = getTimeZone();
    const data = {
      provider,
      time,
      date,
      timeZone,
      visitTypeId,
    };

    try {
      await handleSelectVisit(data);

      navigate(EMAIL_ROUTE_PATHNAME);
    } catch (error) {
      captureException(error);

      if (isAxiosError(error)) {
        if (error.name === 'DateTimeNotAvailable') {
          revalidator.revalidate();

          alert(
            'The visit slot you selected was booked before we could reserve it. Please pick another slot.',
          );
        }
      } else {
        alert('An unknown error occurred. Please try again.');
      }
    }
  }

  return (
    <div className="grid gap-sp-2">
      <div className="grid gap-sp-1">
        {availabilities.firstVisit && (
          <ProviderAvailabilityCard
            {...availabilities.firstVisit}
            onClick={handleClickAvailability}
            slotPosition="top"
          />
        )}

        {availabilities.secondVisit && (
          <ProviderAvailabilityCard
            {...availabilities.secondVisit}
            onClick={handleClickAvailability}
            slotPosition="middle"
          />
        )}

        {availabilities.thirdVisit && (
          <ProviderAvailabilityCard
            {...availabilities.thirdVisit}
            onClick={handleClickAvailability}
            slotPosition="bottom"
          />
        )}
      </div>

      <AllAppointmentsLink />
    </div>
  );
}

function getDateFromTimeBlock(timeBlock: TimeBlock) {
  const now = dayjs();

  if (timeBlock === 'tomorrow') {
    return now.add(1, 'day').format('YYYY-MM-DD');
  }

  return now.format('YYYY-MM-DD');
}
